import { NotificationInfoOutlineIcon } from '@tableau/icons/NotificationInfoOutlineIcon';
import { Tooltip, TooltipPosition } from '@tableau/widgets';
import React from 'react';

import { beaconComponentRegistryEntry, getUserAccountSettingsURL } from '~src/beacon/BeaconComponentRegistryFunctions';
import { DeepLink } from '~src/beacon/deepLink/DeepLink';
import { SessionServerSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';

import { Messages } from '../../codegen/Localize';
import { IServerUserInfo } from '../../userManagement/users/models/IServerUserInfo';
import { ISiteUserInfo } from '../../userManagement/users/models/ISiteUserInfo';
import { UserSettingsStyle } from '../styles/UserSettingsStyle';
import { ChangePassword, ChangePasswordProps } from './ChangePassword';
import { EmailAndDisplayNameForm } from './EmailAndDisplayNameForm';
import { ManageMfaProps, ManageMfaUrl } from './ManageMfa';

const DEEPLINK_ID = 'username';

export interface GeneralAuthSettingsProps {
  //auth families
  isDomainFamilyLocal: boolean;
  isDomainFamilyExternal: boolean;
  isDomainFamilyAD: boolean;
  //data
  emailFormInitialValues: EmailAndDisplayNameForm.FormFields;
  //meta data related to business logic
  updateDisplayNameAllowed: boolean;
  siteUser: ISiteUserInfo;
  serverUser: IServerUserInfo;
  isViewerServerAdmin: boolean;
  isViewerSelf: boolean;
  isUserSelf: boolean;
  changePasswordUrl: string;
  manageMfaUrl: string;
  isViewerAdmin: boolean;
}

function getHeader(){
  return (<h3 className={UserSettingsStyle.titleText}>{Messages.userSettings_Account_Header()}</h3>);
}

export function GeneralAuthSettings({emailFormInitialValues, ...passThroughProps}: GeneralAuthSettingsProps) {
  const emailAndDisplayNameFormProps: EmailAndDisplayNameForm.Props = {
    initialValues: emailFormInitialValues,
    ...passThroughProps,
    handleSubmit: EmailAndDisplayNameForm.handleSubmit
  };
  const changePasswordProps: ChangePasswordProps = {
    ...passThroughProps,

  };
  const manageMfaProps: ManageMfaProps = {
    ...passThroughProps
  };
  const isOnline = SessionServerSelectors.isOnlineEnvironment(getAppStore().getState());
  return (
    <>
    {getHeader()}
    <table>
      <tbody>
        {/* username section */}
        {(passThroughProps.isDomainFamilyAD || passThroughProps.isViewerServerAdmin || passThroughProps.isViewerSelf ||
         ((isOnline && FeatureFlags.usernameEmailDecouplingEnabled && passThroughProps.isViewerAdmin))) &&
          <tr>
            <td className={UserSettingsStyle.tbody} colSpan={3}>
              <DeepLink deepLinkId={DEEPLINK_ID}>
                <table>
                  <tbody>
                    <tr data-tb-test-id="settings-username">
                      <td className={UserSettingsStyle.labelText}>
                        <span className={UserSettingsStyle.labelWithInfoButtonText}>{Messages.username()}</span>
                        <Tooltip
                          tooltipContent={Messages.username_toolTip()}
                          position={TooltipPosition.TopRightCorner}
                          followCursor={false}
                          maxWidth={400}
                          >
                          <NotificationInfoOutlineIcon size={16} alt={''} style={{paddingLeft:'10px'}}/>
                          </Tooltip>
                      </td>
                      <td className="tb-details-value" tb-test-id="tb-username-value-id">{passThroughProps.serverUser.username}</td>
                      <td></td>
                    </tr>
                  </tbody>
                </table>
              </DeepLink>
            </td>
          </tr>
        }
        <tr>
          <td className={UserSettingsStyle.tbody} colSpan={3}>
            <EmailAndDisplayNameForm.Form {...emailAndDisplayNameFormProps}/>
          </td>
        </tr>
        <ChangePassword {...changePasswordProps}/>
        <ManageMfaUrl {...manageMfaProps}/>
      </tbody>
    </table>
    </>
  );
}

export const UsernameSettingBeaconConfig = beaconComponentRegistryEntry({
  command: () =>  Messages.username(),
  deeplinkId: DEEPLINK_ID,
  url: (props) => getUserAccountSettingsURL(props.sessionInfo),
  isAdmin: false,
  // NOTE: Since we direct users to their own page, passThroughProps.isViewerSelf aka
  // getSessionManagementServiceInstance().isSessionUserSelf(username, domainName) will always be true so, we don't
  // need to check passThroughProps.isDomainFamilyAD (which is
  // ServerSettingsUnauthenticated.isServerDomainFamilyActiveDirectory()) nor passThroughProps.isViewerServerAdmin
  // (which is getSessionManagementServiceInstance().isSessionServerAdmin())
  tags: () => { return [Messages.userSettings_Account_Header()]; }
});